// Libs
import { Session, Request } from "@collectivehealth/cog";

// Constants
import { ROLES, ROLE_DISPLAY_LABELS } from "constants/authorization";

export default class SponsorAdmin {
  constructor(data = {}) {
    this.data = data;
  }

  /**
   * @returns {number}
   */
  get id() {
    return this.data.id;
  }

  /**
   * @returns {string}
   */
  get email() {
    return this.data.email;
  }

  /**
   * @returns {string}
   */
  get uuid() {
    return this.data.userIdentifier;
  }

  /**
   * @returns {string}
   */
  get firstName() {
    return this.data.firstName;
  }

  /**
   * @returns {string}
   */
  get lastName() {
    return this.data.lastName;
  }

  /**
   * @returns {string}
   */
  get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  /**
   * @returns {string}
   */
  get role() {
    return this.data.role;
  }

  /**
   * @returns {array}
   */
  get segmentGroups() {
    return this.data.segmentGroups || [];
  }

  /**
   * @returns {string}
   */
  get sponsorGroupType() {
    return this.data.sponsorGroupType || "";
  }

  /**
   * @returns {string}
   */
  get displaySponsorGroupType() {
    return ROLE_DISPLAY_LABELS[this.sponsorGroupType] || this.sponsorGroupType;
  }

  /**
   * Changes the password of a sponsor admin.
   * @param {string} oldPassword
   * @param {string} password
   * @param {string} passwordConfirm
   * @returns {promise}
   */
  changePassword(oldPassword, password, passwordConfirm) {
    return Request.put(`/api/v1/person/${this.id}/password`, {
      email: this.email,
      oldPassword,
      password,
      passwordConfirm
    });
  }

  /**
   * Deletes this sponsor admin.
   * @returns {promise}
   */
  delete() {
    return Request.delete(`/api/v1/sponsor_admin/${this.id}`);
  }

  /**
   * Updates the sponsor admin's segment groups and/or sponsor group.
   * If one of these two properties is omitted from the body, then that property will not be updated
   * @param {object[]} segmentGroups The segment groups value to set
   * @param {string} sponsorGroupType The sponsor group type value to set (ex: "peopleOnly")
   * @returns {promise}
   */
  update(sponsorGroupType, segmentGroups) {
    return Request.put(`/api/v1/sponsor_admin/${this.id}`, {
      segmentGroups,
      sponsorGroupType
    });
  }

  /**
   * Gets the request config for a single sponsor admin
   * @param {number} uuid UUID of the sponsor admin to request
   */
  static getSingleConfig(uuid = Session.getSession().sub) {
    return {
      url: `/api/v2/sponsor_admin/${uuid}`,
      method: "GET",
      cache: false,
      params: { expand: ["sponsorGroupType", "segmentGroups"] }
    };
  }

  /**
   * @returns {SponsorAdmin} default case for posing user
   */
  static get posingAdmin() {
    return new this({
      email: Session.getSession().email,
      firstName: "CHAdmin",
      lastName: "",
      role: ROLES.CH_ADMIN,
      sponsorGroupType: ROLES.ADMIN
    });
  }
}
